/**
 * Network selector component
 * Updated to support MegaETH testnet
 */
import { CSSProperties, FunctionComponent, SVGProps } from "react";
import { useTranslation } from "react-i18next";
import { ChainIdEnum } from "../../enums/chain";
import { getChainName } from "../../config/chains";
import { NetworkButton, NetworkButtonsContainer } from "./style";

import { ReactComponent as ARBLogo } from "../../assets/svg/assets/ARB.svg";
import { ReactComponent as ETHLogo } from "../../assets/svg/assets/ETH.svg";
import { ReactComponent as MONLogo } from "../../assets/svg/assets/MON.svg";
import { ReactComponent as DOGELogo } from "../../assets/svg/assets/DOGE.svg";
import { ReactComponent as CAMPLogo } from "../../assets/svg/assets/CAMP.svg";
import { ReactComponent as OPLogo } from "../../assets/svg/assets/OP.svg";
import { ReactComponent as USDCLogo } from "../../assets/svg/assets/usdc.svg";
import { supportedSigningChainIds } from "../../utils/wallet/connectors";

type ChainIcon = FunctionComponent<SVGProps<SVGSVGElement>>;

/**
 * Per-chain network icon. This is a network picker so each chain shows its own
 * mark, but the mapping is keyed by chainId (no switch fallthrough) so a chain
 * can never inherit another chain's icon. The visible LABEL is always derived
 * from chain config via getChainName(id) — never hardcoded — so newly added
 * or renamed chains stay correct automatically.
 */
const CHAIN_ICON: Partial<Record<ChainIdEnum, ChainIcon>> = {
  [ChainIdEnum.ETH_MAINNET]: ETHLogo,
  [ChainIdEnum.SEPOLIA_TESTNET]: ETHLogo,
  [ChainIdEnum.BASECAMP_TESTNET]: CAMPLogo,
  [ChainIdEnum.DOGEOS_TESTNET]: DOGELogo,
  [ChainIdEnum.MONAD_TESTNET]: MONLogo,
  [ChainIdEnum.ARC_TESTNET]: USDCLogo,
  [ChainIdEnum.MEGAETH_TESTNET]: ETHLogo,
  [ChainIdEnum.OPTIMISM]: OPLogo,
  [ChainIdEnum.OPTIMISM_TESTNET]: OPLogo,
  [ChainIdEnum.ARBITRUM]: ARBLogo,
  [ChainIdEnum.ARBITRUM_TESTNET]: ARBLogo,
};

interface ISelectMultichainNetworkProps {
  selectedChain: ChainIdEnum;
  onSelectChain: (chainId: ChainIdEnum) => void;
  chains?: ChainIdEnum[];
  style?: CSSProperties;
}

function SelectMultichainNetwork({
  selectedChain,
  onSelectChain,
  chains = supportedSigningChainIds,
  style,
}: ISelectMultichainNetworkProps) {
  const { t } = useTranslation("app", { keyPrefix: "SelectMultichainNetwork" });

  return (
    <NetworkButtonsContainer style={style}>
      {chains.map((id) => {
        const Icon = CHAIN_ICON[id];
        const name = getChainName(id);
        const label = !name || name === "Unknown" ? t("unknown_network") : name;
        return (
          <NetworkButton
            key={String(id)}
            isSelected={selectedChain === id}
            onClick={() => onSelectChain(id)}
          >
            {Icon && <Icon />}
            {label}
          </NetworkButton>
        );
      })}
    </NetworkButtonsContainer>
  );
}

export default SelectMultichainNetwork;
